/**
 * Store headers for a specific service as a single JSON object.
 * @param serviceKey - The service key (e.g., "ewings", "wings", "delve").
 * @param headers - The headers object to store.
 */
export const storeServiceHeaders = (
  serviceKey: string,
  headers: Record<string, any>
) => {
  if (headers && typeof headers === "object") {
    sessionStorage.setItem(`header.${serviceKey}`, JSON.stringify(headers));
  }
};

/**
 * Store all headers from API response.
 * @param headers - The headers object containing all services.
 */
export const storeHeadersFromApi = (headers: Record<string, any>) => {
  if (headers && typeof headers === "object") {
    Object.entries(headers).forEach(([serviceKey, serviceHeaders]) => {
      if (typeof serviceHeaders === "object") {
        storeServiceHeaders(serviceKey, serviceHeaders);
      }
    });
  }
};

/**
 * Retrieve headers for a specific service from sessionStorage.
 * @param serviceKey - The key of the service (e.g., "wings", "ewings", "delve").
 * @returns An object containing the headers for the specified service.
 */
export const getStoredHeaders = (serviceKey: string): Record<string, any> => {
  const storedData = sessionStorage.getItem(`header.${serviceKey}`);
  if (storedData) {
    try {
      return JSON.parse(storedData);
    } catch (error) {
      console.error(`Error parsing stored headers for ${serviceKey}:`, error);
      return {};
    }
  }
  return {};
};

/**
 * Clear all stored headers.
 */
export const clearAllHeaders = () => {
  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith("header.")) {
      sessionStorage.removeItem(key);
    }
  });
};

/**
 * Filter out headers from storedHeaders that already exist in existingHeaders (case-insensitive).
 * @param storedHeaders - Headers retrieved from storage.
 * @param existingHeaders - Headers that are already set.
 * @returns Filtered headers object containing only non-conflicting headers.
 */
export const filterConflictingHeaders = (
  storedHeaders: Record<string, any>,
  existingHeaders: Record<string, any>
): Record<string, any> => {
  if (!storedHeaders || typeof storedHeaders !== "object") {
    return {};
  }

  // Get list of already set header keys (case-insensitive)
  const existingHeaderKeys = Object.keys(existingHeaders).map((key) =>
    key.toLowerCase()
  );

  // Filter out headers that already exist in existingHeaders
  return Object.entries(storedHeaders).reduce((acc, [key, value]) => {
    if (!existingHeaderKeys.includes(key.toLowerCase())) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);
};
